import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The sidebar: the brand, the navigation groups, the `console_version` line and the theme toggle.
 *
 * Drawn to the prototype's App shell (`design/WeTalk.dc.html`, `data-screen-label="App shell"`):
 * 252 px wide, the groups 18 px apart, each row 14 px text with a 17 px glyph, the count badges
 * as tabular figures and "New" as the accent pill. The measurements live in `shell.css`.
 *
 * AMENDED 2026-09-25 (customer-portal review #53, #60, #118, #129, #133, #135):
 *
 *   · the rows are `navigationFor(role)` — a page the signed-in role cannot read is not offered;
 *   · one row is current at a time (`currentEntryLabel`);
 *   · a count that is still loading is a same-width placeholder, not an absent badge;
 *   · the column runs the full height of the page, with its contents pinned to the viewport
 *     (`.c-sidebar-inner`, sticky), so a long page no longer shows the canvas under the sidebar;
 *   · below tablet width the sidebar is an overlay opened by the top bar's menu button, and closes
 *     when a row is chosen or the backdrop is pressed (`open`, `onClose`).
 *
 * AMENDED 2026-10-05 (plan-014 A4): the rows are `navigationFor(scope)` — the workspace role AND the
 * organization's capabilities, so the Organization group appears for an organization role; with the
 * organization alone open there is no workspace card (the prototype hides it, `org.noWorkspace`)
 * and the brand opens the context's landing instead of the dashboard.
 *
 * AMENDED 2026-10-09 (design sync): the workspace card at the foot is gone — the design removed the
 * "manage workspaces" button; the workspace picker in the top bar names the workspace and offers
 * "Manage workspaces". The foot is the console_version line and the theme toggle.
 */
import { Link, useRouterState } from "@tanstack/react-router";
import { Icon, IconButton } from "@wetalk/ui";
import type { ReactNode } from "react";

import { GLYPH } from "./glyph.js";
import {
  currentEntryLabel,
  navigationFor,
  type NavigationCountKey,
  type NavigationEntry,
  type NavigationScope,
} from "./navigation.js";
import { useTheme } from "./theme.js";

export function Brand(): ReactNode {
  usePortalTranslation("shell");
  return (
    <>
      <span className="c-brand-we">we</span>
      <span className="c-brand-talk">talk</span>
    </>
  );
}

/**
 * The sidebar's count badges. A number is drawn; `null` means "still loading" and draws a
 * placeholder of the same width (review #53); an absent key — the read failed, or the role may
 * not make it — draws nothing, never a zero.
 */
export type NavigationCount = Partial<Readonly<Record<NavigationCountKey, number | null | undefined>>>;

interface EntryProps {
  readonly entry: NavigationEntry;
  readonly count: NavigationCount;
  readonly current: boolean;
  readonly onChoose: () => void;
}

function Entry({ entry, count, current, onChoose }: EntryProps): ReactNode {
  usePortalTranslation("shell");
  const figure = entry.count === undefined ? undefined : count[entry.count];

  const badge =
    entry.badgeKind === "count" ? (
      figure === undefined ? null : figure === null ? (
        <span className="c-sidebar-badge c-sidebar-badge-pending" aria-hidden="true">
          0
        </span>
      ) : (
        <span className="c-sidebar-badge">{String(figure)}</span>
      )
    ) : entry.badge === null ? null : (
      <span className={entry.badgeKind === "new" ? "c-sidebar-badge-new" : "c-sidebar-badge"}>
        {entry.badge}
      </span>
    );

  const inner = (
    <>
      <Icon glyph={entry.glyph} size="lg" />
      <span className="c-sidebar-link-label">{entry.label}</span>
      {badge}
    </>
  );

  if (entry.to === null) {
    return (
      <span
        className="c-sidebar-link"
        aria-disabled="true"
        title={portalText("shell", "value_arrives_with_value_1b10624a", { v0: String(entry.label), v1: String(entry.phase ?? "a later phase") })}
      >
        {inner}
      </span>
    );
  }

  return (
    <Link
      to={entry.to}
      className="c-sidebar-link"
      aria-current={current ? "page" : undefined}
      onClick={onChoose}
    >
      {inner}
    </Link>
  );
}

export interface SidebarProps {
  readonly consoleVersion: string;
  /** What the sidebar offers depends on the workspace role and the organization role (#135, plan-014 A4). */
  readonly scope: NavigationScope;
  /** Where the brand goes: the context's landing (the dashboard in a workspace). */
  readonly homePath: string;
  /** The count badges. Omitted, no count is drawn. */
  readonly count?: NavigationCount;
  /** Below tablet width: whether the overlay is open. Ignored on a wide screen. */
  readonly open?: boolean;
  /** Closes the overlay (a row was chosen, or the backdrop pressed). */
  readonly onClose?: () => void;
}

export function Sidebar({
  consoleVersion,
  scope,
  homePath,
  count = {},
  open = false,
  onClose = () => undefined,
}: SidebarProps): ReactNode {
  usePortalTranslation("shell");
  const { theme, toggle } = useTheme();
  const themeLabel = theme === "light" ? portalText("shell", "dark_mode_9cf83d1f") : portalText("shell", "light_mode_3d3791aa");
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const shown = navigationFor(scope);
  const currentLabel = currentEntryLabel(pathname, shown);

  return (
    <>
      {open ? (
        <button
          type="button"
          className="c-sidebar-backdrop"
          aria-label={portalText("shell", "close_the_menu_1b6e954f")}
          onClick={onClose}
        />
      ) : null}
      <nav
        id="c-sidebar"
        className="c-sidebar"
        aria-label={portalText("shell", "nav.portal")}
        data-open={open ? "true" : "false"}
      >
        <div className="c-sidebar-inner">
          <Link
            to={homePath}
            className="c-brand"
            aria-label={homePath === "/dashboard" ? portalText("shell", "brand.dashboard") : undefined}
            onClick={onClose}
          >
            <Brand />
          </Link>

          <div className="c-sidebar-groups">
            {shown.map((group) => (
              <div className="c-sidebar-group" key={group.label}>
                <p className="c-sidebar-group-label">
                  {group.label}
                  <Icon glyph={GLYPH.caretDown} size="xs" />
                </p>
                {group.entry.map((entry) => (
                  <Entry
                    entry={entry}
                    count={count}
                    current={entry.label === currentLabel}
                    onChoose={onClose}
                    key={entry.label}
                  />
                ))}
              </div>
            ))}
          </div>

          <div className="c-sidebar-foot">
            <div className="c-sidebar-meta">
              <span title={portalText("shell", "build.title")}>{consoleVersion}</span>
              {/* No "Changelog" link until wetalk.io is delegated (RC-73, customer-portal review #2):
                  it pointed at a domain WeTalk does not serve yet. */}
              <span className="c-sidebar-meta-spacer">
                <IconButton
                  glyph={theme === "light" ? GLYPH.moon : GLYPH.sun}
                  label={themeLabel}
                  title={themeLabel}
                  size="lg"
                  onClick={toggle}
                />
              </span>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
